import {
	calcModifiedDietz,
	Vault,
	VAULT_SHARES_PRECISION_EXP,
	VaultDepositor,
} from '@velocity-exchange/vaults-sdk';
import {
	BigNum,
	BN,
	PERCENTAGE_PRECISION,
	QUOTE_PRECISION_EXP,
	ZERO,
} from '@velocity-exchange/sdk';
import {
	SerializedVaultDepositorRecord,
	SerializedVaultSnapshot,
} from 'src/db/schema';
import invariant from 'tiny-invariant';
import { ExternalUiVaultConfig, UiVaultConfig } from 'src/@types/vaults';
import dayjs from 'dayjs';
import { VaultStatWithConfig } from 'src/hooks/query/vaults/derived/useVaultStats';
import { MarketId, UIMarket } from '@velocity-exchange/common';

/**
 * Returns the balance of a vault depositor, after fees if `afterFees` is true.
 */
export const getVaultDepositorBalance = (
	vaultDepositorAccountData:
		| (Pick<VaultDepositor, 'vaultShares'> &
				Partial<
					Pick<
						VaultDepositor,
						| 'cumulativeProfitShareAmount'
						| 'netDeposits'
						| 'lastWithdrawRequest'
					>
				>)
		| undefined,
	vaultAccountData: Pick<Vault, 'totalShares' | 'profitShare'> | undefined,
	vaultTvlBase: BigNum | undefined,
	depositAssetPrecisionExp: BN,
	afterFees = false
) => {
	if (
		!vaultDepositorAccountData ||
		!vaultAccountData ||
		!vaultTvlBase ||
		vaultAccountData.totalShares?.eqn(0)
	) {
		return BigNum.from(0, depositAssetPrecisionExp);
	}

	const isInWithdrawalState =
		vaultDepositorAccountData.lastWithdrawRequest?.shares.gtn(0);

	// active vault shares refers to shares that are not being withdrawn, hence still profiting from the vault
	const userActiveVaultSharesBN = isInWithdrawalState
		? vaultDepositorAccountData.vaultShares.sub(
				vaultDepositorAccountData.lastWithdrawRequest?.shares ?? ZERO
			)
		: vaultDepositorAccountData.vaultShares;
	const userActiveVaultShares = BigNum.from(
		userActiveVaultSharesBN,
		VAULT_SHARES_PRECISION_EXP
	);
	const userNonActiveVaultShares = isInWithdrawalState
		? BigNum.from(
				vaultDepositorAccountData.lastWithdrawRequest?.shares,
				VAULT_SHARES_PRECISION_EXP
			)
		: BigNum.zero(VAULT_SHARES_PRECISION_EXP);

	const totalVaultShares = BigNum.from(
		vaultAccountData.totalShares,
		VAULT_SHARES_PRECISION_EXP
	);

	const userActiveBalanceBaseValue = userActiveVaultShares
		.mul(vaultTvlBase)
		.div(totalVaultShares)
		.shiftTo(depositAssetPrecisionExp);
	const userNonActiveBalanceBaseValue = userNonActiveVaultShares
		.mul(vaultTvlBase)
		.div(totalVaultShares)
		.shiftTo(depositAssetPrecisionExp);
	// withdrawal amount is capped at the value of the last withdraw request
	const userMaxNonActiveBalanceBaseValue = isInWithdrawalState
		? BigNum.min(
				userNonActiveBalanceBaseValue,
				BigNum.from(
					vaultDepositorAccountData.lastWithdrawRequest?.value,
					depositAssetPrecisionExp
				)
			)
		: userNonActiveBalanceBaseValue;

	if (afterFees) {
		invariant(
			vaultDepositorAccountData.cumulativeProfitShareAmount,
			'Need to provide cumulativeProfitShareAmount to calculate balance after fees'
		);
		invariant(
			vaultDepositorAccountData.netDeposits,
			'Need to provide netDeposits to calculate balance after fees'
		);

		const highWaterMark =
			vaultDepositorAccountData.cumulativeProfitShareAmount.add(
				vaultDepositorAccountData.netDeposits
			);

		const taxableGains = userActiveBalanceBaseValue.sub(
			BigNum.from(highWaterMark, depositAssetPrecisionExp)
		);

		if (!taxableGains.gtZero()) {
			return userActiveBalanceBaseValue.add(userMaxNonActiveBalanceBaseValue);
		}

		const feesPayable = taxableGains.scale(
			new BN(vaultAccountData.profitShare),
			PERCENTAGE_PRECISION
		);
		const userActiveBalanceAfterFees =
			userActiveBalanceBaseValue.sub(feesPayable);

		return userActiveBalanceAfterFees.add(userMaxNonActiveBalanceBaseValue);
	}

	return userActiveBalanceBaseValue.add(userMaxNonActiveBalanceBaseValue);
};

export const getMaxDailyDrawdownFromHistory = (
	vaultSnapshots: Pick<
		SerializedVaultSnapshot,
		'ts' | 'totalAccountBaseValue' | 'netDeposits' | 'totalAccountQuoteValue'
	>[],
	valueField: 'totalAccountBaseValue' | 'totalAccountQuoteValue'
) => {
	const formattedSnapshots = vaultSnapshots.map((snapshot) => {
		const basePnl = new BN(snapshot.totalAccountBaseValue).sub(
			new BN(snapshot.netDeposits)
		);

		return {
			ts: +snapshot.ts,
			basePnl,
			tvlBase: new BN(snapshot[valueField]),
		};
	});

	const sortedSnapshots = [...formattedSnapshots].sort((a, b) => a.ts - b.ts);
	let maxDrawdown = 0;

	for (let i = 0; i < sortedSnapshots.length - 1; i++) {
		const currentDayAllTimeDayPnl = sortedSnapshots[i].basePnl;
		const previousDayAllTimeDayPnl = sortedSnapshots[i - 1]?.basePnl ?? ZERO;

		if (currentDayAllTimeDayPnl > previousDayAllTimeDayPnl) continue; // made profit for that day; no drawdown

		const currentDayPnl = currentDayAllTimeDayPnl.sub(previousDayAllTimeDayPnl);
		const currentDayTotalAccValue = sortedSnapshots[i].tvlBase;

		if (currentDayTotalAccValue.eqn(0)) {
			continue;
		}

		const drawdown =
			currentDayPnl
				.mul(PERCENTAGE_PRECISION)
				.div(currentDayTotalAccValue.sub(currentDayPnl))
				.toNumber() / PERCENTAGE_PRECISION.toNumber();

		if (drawdown < maxDrawdown) {
			maxDrawdown = drawdown;
		}
	}

	return maxDrawdown;
};

// if undefined, use the actual apy value
const APY_CLAMP = {
	min: 0,
	max: 100,
};

export const getApyToUse = (
	uiVaultConfig: UiVaultConfig,
	calculatedApy: number,
	numOfVaultSnapshots: number
) => {
	const isUsingTemporaryApy =
		uiVaultConfig.temporaryApy && numOfVaultSnapshots < 30;

	const apy = isUsingTemporaryApy ? uiVaultConfig.temporaryApy : calculatedApy;
	const isExceedingMaxApy = apy > APY_CLAMP.max;

	const apyString =
		isExceedingMaxApy && isUsingTemporaryApy
			? `> ${APY_CLAMP.max.toFixed(2)}%` // don't allow vault managers to over-optimistically set target apy
			: `${apy.toFixed(2)}%`;

	return {
		isUsingTemporaryApy,
		apy,
		apyString,
	};
};

export const getVaultApyDays = (numOfVaultSnapshots: number) => {
	const numOfDays = Math.min(numOfVaultSnapshots, 90);

	if (numOfDays < 2) {
		return `${numOfDays} day`;
	} else {
		return `${numOfDays} days`;
	}
};

export const getVaultDepositorNotionalNetDeposits = (
	vaultDepositorHistory: SerializedVaultDepositorRecord[]
) => {
	const notionalNetDeposits = vaultDepositorHistory.reduce(
		(acc, record) => {
			const notionalBigNum = BigNum.from(
				record.notionalValue,
				QUOTE_PRECISION_EXP
			);

			if (record.action === 'deposit') {
				return acc.add(notionalBigNum);
			} else if (record.action === 'withdraw') {
				return acc.sub(notionalBigNum);
			} else {
				return acc;
			}
		},
		BigNum.from(0, QUOTE_PRECISION_EXP)
	);

	return notionalNetDeposits;
};

export const getVaultAge = (vaultStat: VaultStatWithConfig): number => {
	if (vaultStat.numOfVaultSnapshots) {
		return vaultStat.numOfVaultSnapshots;
	}
	return !vaultStat.uiVaultConfig.isVelocityVaultsProgramVault
		? dayjs().diff(
				dayjs.unix((vaultStat.uiVaultConfig as ExternalUiVaultConfig).startTs),
				'days'
			)
		: 0;
};

export const getAuthorityVaultBalance = (
	vaultDepositorAccounts: VaultDepositor[],
	vaultStat: VaultStatWithConfig,
	type: 'base' | 'quote',
	getOraclePrice?: (marketId: MarketId | undefined) => BigNum | undefined
) => {
	const basePrecisionExp =
		UIMarket.spotMarkets[vaultStat.uiVaultConfig.depositAsset].precisionExp;

	const vaultDepositor = vaultDepositorAccounts
		.filter((acc) => !!acc)
		.find(
			(account) =>
				account.vault.toString() === vaultStat.uiVaultConfig.vaultPubkeyString
		);

	const vaultDepositorBalance = vaultDepositor
		? getVaultDepositorBalance(
				vaultDepositor,
				{
					totalShares: vaultStat.totalShares.val,
					profitShare: vaultStat.profitShare,
				},
				vaultStat.tvlBase,
				basePrecisionExp,
				true
			)
		: BigNum.zero(basePrecisionExp);

	if (type === 'quote' && getOraclePrice) {
		const marketId = MarketId.createSpotMarket(
			vaultStat.uiVaultConfig.depositAsset
		);

		const oraclePrice = getOraclePrice(marketId);

		return vaultDepositorBalance
			.mul(oraclePrice ?? ZERO)
			.shiftTo(QUOTE_PRECISION_EXP);
	}

	return vaultDepositorBalance;
};

export function getTimeWeightedRoi(
	vaultDepositorAccountData: VaultDepositor | undefined,
	vaultAccountData: Vault,
	uiVaultConfig: UiVaultConfig,
	vaultDepositorHistory: SerializedVaultDepositorRecord[],
	depositAssetPrecisionExp: BN,
	currentUserBalance: BigNum
) {
	if (
		!vaultDepositorAccountData ||
		vaultDepositorAccountData.totalDeposits.eqn(0) ||
		!vaultAccountData
	)
		return 0;

	const formattedHistory = vaultDepositorHistory
		.filter((r) => r.action === 'deposit' || r.action === 'withdraw')
		.map((deposit) => {
			return {
				ts: deposit.ts,
				marketIndex: deposit.spotMarketIndex, // not needed
				amount: uiVaultConfig.isNotionalGrowthStrategy
					? deposit.notionalValue
					: deposit.amount,
				direction: deposit.action as 'deposit' | 'withdraw',
			};
		})
		.sort((a, b) => +b.ts - +a.ts); // need to sort in descending order

	if (uiVaultConfig.isNotionalGrowthStrategy) {
		const { returns } = calcModifiedDietz(
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			currentUserBalance as any,
			depositAssetPrecisionExp,
			formattedHistory
		);

		return returns * 100;
	} else {
		const { returns } = calcModifiedDietz(
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			currentUserBalance as any,
			depositAssetPrecisionExp,
			formattedHistory
		);

		return returns * 100;
	}
}

export function getTimeWeightedAPY(
	vaultDepositorAccountData: VaultDepositor,
	vaultAccountData: Vault,
	uiVaultConfig: UiVaultConfig,
	vaultDepositorHistory: SerializedVaultDepositorRecord[],
	depositAssetPrecisionExp: BN,
	currentUserBalance: BigNum
) {
	if (
		!vaultDepositorAccountData ||
		vaultDepositorAccountData.totalDeposits.eqn(0) ||
		!vaultAccountData
	)
		return 0;

	// Get the time-weighted ROI
	const roi = getTimeWeightedRoi(
		vaultDepositorAccountData,
		vaultAccountData,
		uiVaultConfig,
		vaultDepositorHistory,
		depositAssetPrecisionExp,
		currentUserBalance
	);

	// Convert ROI to decimal
	const roiDecimal = roi / 100;

	// Get the time period in days
	const firstDepositTs = Math.min(
		...vaultDepositorHistory
			.filter((r) => r.action === 'deposit' || r.action === 'withdraw')
			.map((r) => +r.ts)
	);
	const daysElapsed = (Date.now() / 1000 - firstDepositTs) / (24 * 60 * 60);

	// If we don't have enough history or days elapsed is 0, return 0
	if (daysElapsed <= 0) {
		return 0;
	}

	// Convert ROI to APY using the formula: APY = (1 + ROI)^(365/days) - 1
	const apy = (Math.pow(1 + roiDecimal, 365 / daysElapsed) - 1) * 100;

	return apy;
}

// APY is annualized return with compounding, net of performance fee
// APY = (1 + grossReturn * (1 - perfFeePct))^(365/days) - 1 for positive returns
export function calculateApy(
	startValue: number,
	endValue: number,
	daysElapsed: number,
	perfFeeFraction: number,
	managementFeeFraction: number
): number {
	if (!startValue || !endValue) return 0;
	// Guard against a zero/negative elapsed period: 365 / 0 = Infinity and
	// Math.pow(1, Infinity) = NaN, which renders as a NaN APY (e.g. the first
	// vault-history data point where index === 0, or a near-instant snapshot in
	// the APY cron). Mirrors the daysElapsed guard in getTimeWeightedAPY above.
	if (daysElapsed <= 0) return 0;
	const grossReturn = (endValue - startValue) / startValue;
	const managementFeeProportion = managementFeeFraction * (daysElapsed / 365);
	const netReturn =
		grossReturn > 0
			? grossReturn * (1 - perfFeeFraction - managementFeeProportion)
			: grossReturn;
	const annualizedReturn = Math.pow(1 + netReturn, 365 / daysElapsed) - 1;
	return annualizedReturn;
}
